import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Button,
  Collapse,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoIcon from '@mui/icons-material/Info';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import { fetchSuggestedTopics, fetchSegments } from '../utils/api';

const SuggestedTopics = ({ courseId, courseName, onAddTopic, onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [expanded, setExpanded] = useState(true);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [detailDialog, setDetailDialog] = useState(false);
  const [segmentContents, setSegmentContents] = useState({});
  const [loadingSegments, setLoadingSegments] = useState({});

  const loadSuggestions = async () => {
    if (!courseId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetchSuggestedTopics(courseId);
      setSuggestions(response.data.suggested_topics || []);
    } catch (err) {
      console.error('Failed to fetch suggested topics:', err);
      setError('Failed to load suggested topics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSuggestions();
  }, [courseId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleShowDetails = (suggestion) => {
    setSelectedSuggestion(suggestion);
    setDetailDialog(true);
  };

  const handleAddSuggestion = (suggestion) => {
    if (onAddTopic) {
      // Parse the suggested path to determine parent path
      const pathParts = suggestion.suggested_path.split('/');
      const parentPath = pathParts.length > 1 
        ? pathParts.slice(0, -1).join('/') 
        : null;
      
      // Call the add topic handler with pre-filled data
      onAddTopic(courseId, parentPath, pathParts[pathParts.length - 1]);
    }
    setDetailDialog(false);
  };

  const handleRefresh = async () => {
    await loadSuggestions();
    if (onRefresh) {
      onRefresh();
    }
  };

  const loadSegmentContent = async (segmentId) => {
    // Check if already loaded
    if (segmentContents[segmentId] || loadingSegments[segmentId]) {
      return;
    }

    setLoadingSegments(prev => ({ ...prev, [segmentId]: true }));

    try {
      const response = await fetchSegments();
      const segment = response.data.find(s => s.id === segmentId);
      
      if (segment) {
        setSegmentContents(prev => ({
          ...prev,
          [segmentId]: segment.content
        }));
      }
    } catch (err) {
      console.error(`Failed to load segment ${segmentId}:`, err);
      setSegmentContents(prev => ({
        ...prev,
        [segmentId]: 'Failed to load content'
      }));
    } finally {
      setLoadingSegments(prev => ({ ...prev, [segmentId]: false }));
    }
  };

  if (!suggestions.length && !loading) {
    return null; // Don't show anything if no suggestions
  }

  return (
    <Card sx={{ mb: 2, bgcolor: 'background.paper' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: expanded ? 2 : 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LightbulbIcon color="warning" />
            <Typography variant="h6">
              Suggested Topics
              {suggestions.length > 0 && (
                <Badge badgeContent={suggestions.length} color="warning" sx={{ ml: 2 }}>
                  <span />
                </Badge>
              )}
            </Typography>
          </Box>
          <Box>
            <Tooltip title="Refresh suggestions">
              <IconButton size="small" onClick={handleRefresh} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={() => setExpanded(!expanded)}>
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        <Collapse in={expanded}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : suggestions.length > 0 ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                These topics were suggested by the AI categorizer when it couldn't find appropriate matches:
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {suggestions.map((suggestion) => (
                  <Card key={suggestion.suggested_path} variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                          {suggestion.suggested_path}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                          <Chip 
                            label={`${suggestion.segment_count} segment${suggestion.segment_count > 1 ? 's' : ''}`} 
                            size="small" 
                            color="warning"
                          />
                          <Tooltip title="View details">
                            <IconButton 
                              size="small" 
                              onClick={() => handleShowDetails(suggestion)}
                            >
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                      <Tooltip title={`Add "${suggestion.suggested_path}" to this course`}>
                        <IconButton 
                          color="primary" 
                          onClick={() => handleAddSuggestion(suggestion)}
                        >
                          <AddIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Card>
                ))}
              </Box>

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  After adding suggested topics, you can re-process the affected segments with the <code>--force</code> flag.
                </Typography>
              </Alert>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No topic suggestions available for this course.
            </Typography>
          )}
        </Collapse>
      </CardContent>

      {/* Detail Dialog */}
      <Dialog 
        open={detailDialog} 
        onClose={() => {
          setDetailDialog(false);
          setSegmentContents({});
          setLoadingSegments({});
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Suggested Topic Details
        </DialogTitle>
        <DialogContent>
          {selectedSuggestion && (
            <>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {selectedSuggestion.suggested_path}
              </Typography>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                This topic was suggested for {selectedSuggestion.segment_count} segment{selectedSuggestion.segment_count > 1 ? 's' : ''}.
              </Typography>

              {selectedSuggestion.reason && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Reason from AI:
                  </Typography>
                  <Alert severity="info" sx={{ mb: 1 }}>
                    <Typography variant="body2">{selectedSuggestion.reason}</Typography>
                  </Alert>
                </>
              )}

              <Divider sx={{ my: 2 }} />
              
              {selectedSuggestion.segment_ids && selectedSuggestion.segment_ids.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>
                    Content Segments ({selectedSuggestion.segment_ids.length}):
                  </Typography>
                  
                  {selectedSuggestion.segment_ids.map((segmentId) => (
                    <Accordion 
                      key={segmentId}
                      onChange={(event, isExpanded) => {
                        if (isExpanded) {
                          loadSegmentContent(segmentId);
                        }
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>Segment ID: {segmentId}</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        {loadingSegments[segmentId] ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <CircularProgress size={24} />
                          </Box>
                        ) : segmentContents[segmentId] ? (
                          <Box sx={{ 
                            bgcolor: 'grey.100', 
                            p: 2, 
                            borderRadius: 1,
                            fontFamily: 'monospace',
                            fontSize: '0.875rem',
                            whiteSpace: 'pre-wrap',
                            maxHeight: 300,
                            overflow: 'auto'
                          }}>
                            {segmentContents[segmentId]}
                          </Box>
                        ) : (
                          <Typography color="text.secondary">
                            Click to load content...
                          </Typography>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog(false)}>
            Close
          </Button>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={() => handleAddSuggestion(selectedSuggestion)}
          >
            Add Topic
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default SuggestedTopics;